import { NextPage } from 'next';
import React, { useState, FC } from 'react';
import JsonLd from '../../../components/layouts/JsonLd';
import MetaTags from '../../../components/layouts/MetaTags';
import { MerchantSite } from '../../../components/MerchantSite';
import MerchantBookingServiceShowPage from '../../../templates/merchants/resources/DetailTemplate/MerchantBookingServiceShowPage';
import PasswordPage from '../../../molecules/merchant/resources/book/MerchantCourseDetailsPageProtected';
import ErrorTemplate from '../../../templates/ErrorTemplate';
import {
  allCategoriesCache,
  getCategory,
} from '../../../infrastructure/api/backend/explore_api';
import { BookingPageRepositoryForSSR } from '../../../infrastructure/repositories/BookingPageRepository';
import { CategoryEntity } from '../../../interfaces/explore/CategoryEntity';
import { MerchantDetail } from '../../../interfaces/merchants';
import {
  BookingPageScheme,
  MerchantBookingPageApiResponse,
} from '../../../interfaces/merchants/bookings';
import { MerchantBookingCourse } from '../../../interfaces/merchants/bookings/courses';
import { MerchantBookingStaffApi } from '../../../interfaces/merchants/bookings/staff';
import { SettingCompletion } from '../../../interfaces/merchants/resources/books';
import { MerchantReview } from '../../../interfaces/merchants/reviews';
import { toSettingConfirm } from '../../../redux/reducers/merchants/resources/books/helper';

import {
  fetchMerchantDetail,
  fetchMerchantReviewsForSSR,
} from '../../../infrastructure/api';
import { sanitizeResourcePublicId } from '../../../utils/sanitizeResourcePublicId';
import {
  AppGetServerSidePropsContext,
  injectAppContextGetServerSideProps,
} from '../../../pages/_app';
import { usePushMerchantDataLayerEffect } from '../../../hooks/gtm';
import {
  getServerSideApiClient,
  isAxiosError,
} from '../../../openapi/axiosClient';
import { toThumbnailUrl } from 'src/utils/metatag';
import { Client } from 'src/openapi/generated/apiClient';
import { AxiosRequestConfig } from 'axios';
import {
  EventSchemeResource,
  CourseSchemeResource,
} from 'src/interfaces/reservation_flow';
import { buildSiteUrl } from 'src/utils/request-url';

interface Context extends AppGetServerSidePropsContext {
  query: {
    publicId: string;
    resource_id: string;
  };
}

interface PageProps {
  category?: CategoryEntity;
  bookingData: MerchantBookingPageApiResponse;
  isBookable: boolean;
  reviews?: MerchantReview[];
  settings: SettingCompletion;
  merchantDetail?: MerchantDetail;
  categories: CategoryEntity[];
  courses: MerchantBookingCourse[];
  staff: MerchantBookingStaffApi[];
  isV2Flow: boolean;
  resource?: EventSchemeResource | CourseSchemeResource;
}

interface Props {
  session: string;
  token: string;
  requestId: string;
  isPasswordProtected: boolean;
  isBookable: boolean;
  isUnpublishedSite: boolean;
  pageProps?: PageProps;
  merchantPublicId: string;
  resourcePublicId: string;
  isCoubicApp: boolean;
}

const MerchantResourceComponent: FC<{
  state?: PageProps;
  isBookable: boolean;
  isCoubicApp: boolean;
}> = (props) => {
  const { state, isBookable } = props;
  usePushMerchantDataLayerEffect({
    visit_merchant_id: state?.merchantDetail?.id,
  });
  if (!state) {
    return null;
  }

  const {
    merchantDetail,
    category,
    reviews,
    bookingData,
    settings,
    courses,
    staff,
    isV2Flow,
    resource,
  } = state;

  if (merchantDetail && category && reviews) {
    return (
      <>
        <MetaTags
          title={state.bookingData.name}
          description={state.bookingData.description_text}
          keywords={merchantDetail.seo_setting?.keyword || ''}
          thumbnailUrl={toThumbnailUrl({
            merchantDetail,
            bookingData: state.bookingData,
          })}
          currentPath={buildSiteUrl(`/${state.bookingData.public_id}`)}
          noIndex={merchantDetail.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchantDetail.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchantDetail?.favicon_image?.url}
        />
        <JsonLd
          bookingData={bookingData}
          merchant={merchantDetail}
          reviews={reviews}
        />
        <MerchantBookingServiceShowPage
          bookingData={bookingData}
          isBookable={isBookable}
          reviews={reviews}
          settings={settings}
          merchantDetail={merchantDetail}
          courses={courses}
          staff={staff}
          isCoubicApp={props.isCoubicApp}
          isV2Flow={isV2Flow}
          resource={resource}
        />
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantResource: NextPage<Props> = (props) => {
  const {
    session,
    token,
    requestId,
    isPasswordProtected,
    isBookable,
    isUnpublishedSite,
    resourcePublicId,
    merchantPublicId,
    pageProps,
    isCoubicApp,
  } = props;
  const [passwordProtected, setPasswordProtected] =
    useState<boolean>(isPasswordProtected);
  const [state] = useState<PageProps | undefined>(pageProps);

  const onPasswordSubmit = async (success: boolean) => {
    if (success) {
      setPasswordProtected(false);
      window.location.reload();
    }
  };

  return (
    <MerchantSite
      isProtected={passwordProtected}
      protectedElement={
        <>
          <MetaTags
            noIndex={true}
            opgDisabled={true}
            googleAnalyticsFourTrackingId={
              state?.merchantDetail?.available_google_analytics_four_tracking_id
            }
          />
          <PasswordPage
            publicId={merchantPublicId}
            servicePublicId={resourcePublicId}
            onSubmit={onPasswordSubmit}
            session={session}
            token={token}
            requestId={requestId}
          />
        </>
      }
      isUnPublished={isUnpublishedSite ?? false}
      isMerchantInfoExist={!!state?.merchantDetail}
      logoImage={state?.merchantDetail?.logo_image || null}
      merchantPublicId={merchantPublicId}
      displayName={state?.merchantDetail?.display_name || null}
      isCoubicApp={isCoubicApp}
    >
      <MerchantResourceComponent
        state={state}
        isBookable={isBookable}
        isCoubicApp={isCoubicApp}
      />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx: Context) => {
    const { requestId, query, session, token, isCoubicApp } = ctx;
    const { publicId: merchantPublicId, resource_id: maybeResourceId } = query;

    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const resourcePublicId = sanitizeResourcePublicId(maybeResourceId);

    const repository = BookingPageRepositoryForSSR(session, token, requestId);
    const apiClient = getServerSideApiClient(ctx);

    const { bookable, isUnpublishedSite } = await repository.Bookable(
      merchantPublicId,
      resourcePublicId,
    );

    if (isUnpublishedSite) {
      return {
        props: {
          session,
          token,
          requestId,
          isPasswordProtected: !!bookable?.protected,
          isBookable: !!bookable?.accepting,
          resourcePublicId,
          merchantPublicId,
          isUnpublishedSite,
          isCoubicApp,
        },
      };
    }

    const pageProps = bookable?.protected
      ? undefined
      : await fetchPageProps({
          merchant: merchantDetailResponse.merchant,
          session,
          token,
          requestId,
          resourcePublicId,
          merchantPublicId,
          apiClient,
        });

    return {
      props: {
        session,
        token,
        requestId,
        isPasswordProtected: !!bookable?.protected,
        isBookable: !!bookable?.accepting,
        pageProps,
        resourcePublicId,
        merchantPublicId,
        isUnpublishedSite,
        isCoubicApp,
      },
    };
  },
);

const fetchPageProps = async (args: {
  merchant?: MerchantDetail;
  session: string;
  token: string;
  requestId: string;
  resourcePublicId: string;
  merchantPublicId: string;
  apiClient: Client<AxiosRequestConfig>;
}): Promise<PageProps> => {
  const {
    merchant,
    session,
    token,
    requestId,
    resourcePublicId,
    merchantPublicId,
    apiClient,
  } = args;

  const repository = BookingPageRepositoryForSSR(session, token, requestId);

  const sanitizedResourcePublicId = sanitizeResourcePublicId(resourcePublicId);

  const [
    categories,
    bookingData,
    settings,
    review,
    reservationFlowVersionResult,
  ] = await Promise.all([
    // Layoutで取得している部分を徐々に置き換え中
    allCategoriesCache(),
    // axios.getだとcookieが載らないので、BookingPageRepositoryForSSR経由で取得
    repository.Find(merchantPublicId, sanitizedResourcePublicId),
    repository.ConfirmData(merchantPublicId, sanitizedResourcePublicId),
    fetchMerchantReviewsForSSR({
      session,
      token,
      requestId,
      merchantPublicId,
      page: 1,
      perPage: 10,
    }),
    apiClient.getReservationFlowVersion({
      parameter: {
        merchant_public_id: merchantPublicId,
        resource_public_id: sanitizedResourcePublicId,
      },
    }),
  ]).catch((err) => {
    throw err;
  });

  let resource: EventSchemeResource | CourseSchemeResource | undefined =
    undefined;

  const isV2Flow =
    reservationFlowVersionResult?.reservation_flow_version === 'v2';

  try {
    if (bookingData.model_type === BookingPageScheme.EVENT && isV2Flow) {
      resource = await apiClient.getReservationFlowEventSchemeResource({
        parameter: {
          merchant_public_id: merchantPublicId,
          resource_public_id: sanitizedResourcePublicId,
        },
      });
    } else if (
      (bookingData.model_type === BookingPageScheme.AVAILABILITY ||
        bookingData.model_type === BookingPageScheme.ASSIGN) &&
      isV2Flow
    ) {
      resource = await apiClient.getReservationFlowCourseSchemeResource({
        parameter: {
          merchant_public_id: merchantPublicId,
          resource_public_id: sanitizedResourcePublicId,
        },
      });
    }
  } catch (e) {
    // 組織管理者・スタッフの場合はエラーが返ってくるので無視する
    if (!isAxiosError(e) || e.response?.status !== 403) throw e;
  }

  const category = merchant
    ? await getCategory(merchant.category_id)
    : undefined;

  let pageProps: PageProps = {
    merchantDetail: merchant,
    category,
    categories,
    bookingData,
    settings: toSettingConfirm(settings),
    reviews: review?.data,
    courses: [],
    staff: [],
    isBookable: false,
    isV2Flow,
    resource,
  };

  // FIXME: 本当はV2側でisStaffSelectableを考慮したほうがいいかもしれないが他に影響出るかもなのでここでハンドリングする
  if (
    pageProps.bookingData.model_type === BookingPageScheme.ASSIGN ||
    pageProps.bookingData.model_type === BookingPageScheme.AVAILABILITY
  ) {
    const courses = await repository.SearchCourse(
      merchantPublicId,
      sanitizedResourcePublicId,
    );
    pageProps = { ...pageProps, courses: courses.data };
  }

  // M3 or M4 + スタッフの指名が可能なときのみスタッフの取得を行う
  if (
    (pageProps.bookingData.model_type === BookingPageScheme.ASSIGN &&
      pageProps.bookingData.staff_selectable) ||
    pageProps.bookingData.model_type === BookingPageScheme.EVENT
  ) {
    const staff = await repository.SearchStaff(
      merchantPublicId,
      sanitizedResourcePublicId,
    );

    pageProps = { ...pageProps, staff: staff.data };
  }

  return { ...pageProps };
};

export default MerchantResource;
